import '../../i18n/autoTranslate';
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const ImprovementSuggestions = ({ 
  suggestions = [
    {
      id: 1,
      category: 'Communication',
      priority: 'high',
      title: 'Reduce Filler Words',
      description: 'Practice speaking more deliberately to minimize "um", "uh", and "like" usage.',
      actionItems: [
        'Record yourself speaking for 5 minutes daily',
        'Practice the "pause instead of filler" technique',
        'Use the Toastmasters filler word counter app'
      ],
      resources: [
        { type: 'article', title: 'How to Stop Using Filler Words', url: '#' },
        { type: 'video', title: 'Public Speaking Masterclass', url: '#' },
        { type: 'practice', title: 'Filler Word Challenge', url: '#' }
      ],
      estimatedTime: '2-3 weeks',
      difficulty: 'Medium'
    },
    {
      id: 2,
      category: 'Technical Skills',
      priority: 'medium',
      title: 'Enhance Algorithm Explanation',
      description: 'Improve your ability to explain complex algorithms and their time/space complexity.',
      actionItems: [
        'Practice explaining algorithms out loud',
        'Create visual diagrams for complex problems',
        'Study Big O notation thoroughly'
      ],
      resources: [
        { type: 'course', title: 'Data Structures & Algorithms', url: '#' },
        { type: 'practice', title: 'LeetCode Explanation Practice', url: '#' },
        { type: 'book', title: 'Cracking the Coding Interview', url: '#' }
      ],
      estimatedTime: '4-6 weeks',
      difficulty: 'Hard'
    },
    {
      id: 3,
      category: 'Behavioral',
      priority: 'low',
      title: 'Strengthen STAR Method Usage',
      description: 'While you used STAR method well, adding more quantifiable results would enhance impact.',
      actionItems: [
        'Prepare 5-7 STAR stories with metrics',
        'Practice transitioning between STAR components',
        'Include specific numbers and percentages'
      ],
      resources: [
        { type: 'template', title: 'STAR Method Template', url: '#' },
        { type: 'article', title: 'Behavioral Interview Guide', url: '#' },
        { type: 'practice', title: 'STAR Story Builder', url: '#' }
      ],
      estimatedTime: '1-2 weeks',
      difficulty: 'Easy'
    },
    {
      id: 4,
      category: 'Confidence',
      priority: 'medium',
      title: 'Improve Non-Verbal Communication',
      description: 'Work on maintaining consistent posture and eye contact throughout longer interviews.',
      actionItems: [
        'Practice mock interviews with video recording',
        'Set posture reminders during practice sessions',
        'Work on breathing techniques for relaxation'
      ],
      resources: [
        { type: 'video', title: 'Body Language for Interviews', url: '#' },
        { type: 'app', title: 'Posture Reminder App', url: '#' },
        { type: 'practice', title: 'Video Interview Simulator', url: '#' }
      ],
      estimatedTime: '2-3 weeks',
      difficulty: 'Medium'
    }
  ],
  isLoading = false
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedSuggestions, setExpandedSuggestions] = useState(new Set([1]));

  const categories = ['all', 'Communication', 'Technical Skills', 'Behavioral', 'Confidence'];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-400 border-red-500/20 bg-red-500/10';
      case 'medium':
        return 'text-yellow-400 border-yellow-500/20 bg-yellow-500/10';
      case 'low':
        return 'text-green-400 border-green-500/20 bg-green-500/10';
      default:
        return 'text-gray-400 border-gray-600 bg-gray-800/20';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy':
        return 'text-green-400';
      case 'Medium':
        return 'text-yellow-400';
      case 'Hard':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'article':
        return 'FileText';
      case 'video':
        return 'Play';
      case 'course':
        return 'GraduationCap';
      case 'practice':
        return 'Target';
      case 'book':
        return 'Book';
      case 'app':
        return 'Smartphone';
      case 'template':
        return 'Layout';
      default:
        return 'ExternalLink';
    }
  };

  const toggleSuggestion = (id) => {
    const newExpanded = new Set(expandedSuggestions);
    if (newExpanded?.has(id)) {
      newExpanded?.delete(id);
    } else {
      newExpanded?.add(id);
    }
    setExpandedSuggestions(newExpanded);
  };

  const filteredSuggestions = selectedCategory === 'all' 
    ? suggestions 
    : suggestions?.filter(s => s?.category === selectedCategory);

  if (isLoading) {
    return (
      <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
        <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10 p-6 animate-pulse">
          <div className="space-y-6">
            <div className="h-6 bg-gray-700/50 rounded w-48"></div>
            <div className="flex space-x-2">
              {[1, 2, 3, 4]?.map(i => (
                <div key={i} className="h-8 bg-gray-700/50 rounded w-20"></div>
              ))}
            </div>
            <div className="space-y-4">
              {[1, 2]?.map(i => (
                <div key={i} className="h-32 bg-gray-700/50 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </SpotlightCard>
    );
  }

  return (
    <SpotlightCard spotlightColor="rgba(139, 92, 246, 0.15)">
      <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">AI Improvement Suggestions</h3>
            <p className="text-sm text-gray-400 mt-1">
              Personalized recommendations to enhance your interview performance
            </p>
          </div>
          <Icon name="Target" size={24} className="text-indigo-400" />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories?.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
                selectedCategory === category
                  ? 'bg-indigo-500 text-white shadow-lg border-indigo-500'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white border-gray-700'
              }`}
            >
              {category === 'all' ? 'All Categories' : category}
            </button>
          ))}
        </div>

        {/* Suggestions List */}
        <div className="space-y-4">
          {filteredSuggestions?.map((suggestion) => {
            const isExpanded = expandedSuggestions?.has(suggestion?.id);
            
            return (
              <div key={suggestion?.id} className="border border-gray-700  overflow-hidden bg-gray-800/20">
                <button
                  onClick={() => toggleSuggestion(suggestion?.id)}
                  className="w-full p-6 text-left hover:bg-white/5 transition-all duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(suggestion?.priority)}`}>
                          {suggestion?.priority?.toUpperCase()} PRIORITY
                        </span>
                        <span className="text-xs text-gray-400">{suggestion?.category}</span>
                      </div>
                      
                      <div>
                        <h4 className="text-lg font-semibold text-white">{suggestion?.title}</h4>
                        <p className="text-sm text-gray-300 mt-1">{suggestion?.description}</p>
                      </div>

                      <div className="flex items-center space-x-4 text-xs text-gray-400">
                        <div className="flex items-center space-x-1">
                          <Icon name="Clock" size={12} />
                          <span>{suggestion?.estimatedTime}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="BarChart3" size={12} />
                          <span className={getDifficultyColor(suggestion?.difficulty)}>
                            {suggestion?.difficulty}
                          </span>
                        </div>
                      </div>
                    </div>

                    <Icon 
                      name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                      size={20} 
                      className="text-gray-400 transition-transform duration-200 ml-4"
                    />
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-6 pb-6 animate-fade-in">
                    <div className="border-t border-gray-700 pt-6 space-y-6">
                      {/* Action Items */}
                      <div>
                        <h5 className="font-semibold text-white mb-3 flex items-center space-x-2">
                          <Icon name="CheckSquare" size={16} className="text-indigo-400" />
                          <span>Action Items</span>
                        </h5>
                        <ul className="space-y-2">
                          {suggestion?.actionItems?.map((item, index) => (
                            <li key={index} className="flex items-start space-x-3 text-sm text-gray-300">
                              <Icon name="ArrowRight" size={12} className="text-indigo-400 mt-1 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Resources */}
                      <div>
                        <h5 className="font-semibold text-white mb-3 flex items-center space-x-2">
                          <Icon name="BookOpen" size={16} className="text-indigo-400" />
                          <span>Recommended Resources</span>
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {suggestion?.resources?.map((resource, index) => (
                            <a
                              key={index}
                              href={resource?.url}
                              className="flex items-center space-x-3 p-3 rounded-lg bg-gray-800/30 hover:bg-gray-700/40 border border-gray-700/50 transition-all duration-200 group"
                            >
                              <Icon 
                                name={getResourceIcon(resource?.type)} 
                                size={16} 
                                className="text-indigo-400 group-hover:text-indigo-300" 
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colors duration-200">
                                  {resource?.title}
                                </p>
                                <p className="text-xs text-gray-400 capitalize">
                                  {resource?.type}
                                </p>
                              </div>
                              <Icon name="ExternalLink" size={12} className="text-gray-400" />
                            </a>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-3 pt-4 border-t border-gray-700">
                        <Button variant="default" size="sm" iconName="Plus" className="bg-gradient-to-r from-purple-500 to-indigo-600">
                          Add to Practice Plan
                        </Button>
                        <Button variant="outline" size="sm" iconName="Bookmark" className="border-gray-600 text-gray-300 hover:text-white">
                          Save for Later
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {filteredSuggestions?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="CheckCircle" size={48} className="text-green-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-white mb-2">Great Job!</h4>
            <p className="text-gray-400">
              No specific improvements needed in this category. Keep up the excellent work!
            </p>
          </div>
        )}
      </div>
    </SpotlightCard>
  );
};

export default ImprovementSuggestions;
